import type { AnalyticsEventName, AnalyticsEventProperties } from '@powerful/config/analytics/events';
import {
  createAnalyticsTracker,
  type AnalyticsClient,
  type AnalyticsTracker,
  type TrackOptions,
  type TrackResult,
} from '@powerful/config/analytics/track';
import {
  initPostHog,
  optOutPostHog,
  type PostHogLifecycleState,
} from '@powerful/config/observability/posthog';

export interface PostHogAnalyticsClient extends AnalyticsClient {
  /** Só o id: o segundo argumento do SDK (`$set`) grava atributos de pessoa e fica fora do contrato. */
  identify(distinctId: string): void;
  init(apiKey: string, options: Record<string, unknown>): void;
  opt_in_capturing(): void;
  opt_out_capturing(): void;
  reset(resetDeviceId?: boolean): void;
  isFeatureEnabled(flag: string): boolean | undefined;
  getFeatureFlagPayload(flag: string): unknown;
}

export type AnalyticsActivationResult =
  | { status: 'active'; mode: 'initialized' | 'reactivated' | 'already_active' }
  | { status: 'disabled' }
  | { status: 'missing_key' }
  | { status: 'failed' };

export interface AnalyticsRuntime {
  setStatisticsConsent(consented: boolean): Promise<AnalyticsActivationResult>;
  track<Name extends AnalyticsEventName>(
    name: Name,
    properties: AnalyticsEventProperties[Name],
    options?: TrackOptions,
  ): TrackResult;
  /**
   * Liga os eventos seguintes ao utilizador autenticado (T1.18, ADR 0038). Sem consentimento o
   * pedido fica guardado e é aplicado quando — e se — o consentimento chegar. Nunca lança.
   */
  identify(userId: string): void;
  /** Logout, troca de utilizador ou exclusão: o próximo utilizador do dispositivo não herda nada. */
  resetIdentity(): void;
  getClient(): PostHogAnalyticsClient | null;
}

export interface CreateAnalyticsRuntimeInput {
  loadSdk: () => Promise<PostHogAnalyticsClient>;
  config: { apiKey: string; host: string };
  onError?: (error: unknown) => void;
}

/** Ciclo de vida consent-aware do singleton PostHog usado pelo app. */
export function createAnalyticsRuntime({
  loadSdk,
  config,
  onError = () => undefined,
}: CreateAnalyticsRuntimeInput): AnalyticsRuntime {
  let statisticsConsent = false;
  let sdkClient: PostHogAnalyticsClient | null = null;
  let activationPromise: Promise<AnalyticsActivationResult> | null = null;
  const lifecycleState: PostHogLifecycleState = {
    jaIniciado: false,
    inicializadoAntes: false,
  };
  const tracker: AnalyticsTracker = createAnalyticsTracker({
    hasStatisticsConsent: () => statisticsConsent,
  });
  // O utilizador que deve estar identificado e o que o SDK de facto recebeu. Separados porque o
  // pedido pode chegar antes do consentimento, e a revogação apaga a identidade no SDK.
  let desiredUserId: string | null = null;
  let appliedUserId: string | null = null;

  const capturing = () => statisticsConsent && sdkClient !== null && lifecycleState.jaIniciado;

  const applyIdentity = () => {
    if (!capturing() || !desiredUserId || appliedUserId === desiredUserId) return;
    try {
      sdkClient?.identify(desiredUserId);
      appliedUserId = desiredUserId;
    } catch (error) {
      onError(error);
    }
  };

  const activateClient = (client: PostHogAnalyticsClient): AnalyticsActivationResult => {
    try {
      const result = initPostHog({
        consentimento: statisticsConsent,
        config,
        iniciar: (apiKey, options) => client.init(apiKey, options),
        reativar: () => client.opt_in_capturing(),
        estado: lifecycleState,
      });

      if (!result.iniciado) {
        if (result.motivo === 'ja_iniciado') {
          tracker.setClient(client);
          applyIdentity();
          return { status: 'active', mode: 'already_active' };
        }
        if (result.motivo === 'sem_consentimento') return { status: 'disabled' };
        if (result.motivo === 'sem_chave') return { status: 'missing_key' };
        return { status: 'failed' };
      }

      // Identifica antes de esvaziar a fila: os eventos pendentes já saem ligados ao utilizador.
      applyIdentity();
      tracker.setClient(client);
      return {
        status: 'active',
        mode: result.modo === 'inicializado' ? 'initialized' : 'reactivated',
      };
    } catch (error) {
      onError(error);
      return { status: 'failed' };
    }
  };

  return {
    async setStatisticsConsent(consented: boolean): Promise<AnalyticsActivationResult> {
      statisticsConsent = consented;

      if (!consented) {
        tracker.clearPending();
        tracker.setClient(null);
        if (sdkClient && lifecycleState.jaIniciado) {
          optOutPostHog({ cliente: sdkClient, estado: lifecycleState });
        }
        // A revogação fez reset(true): a identidade no SDK deixou de existir. O pedido fica, para
        // um reconsentimento de quem continua autenticado.
        appliedUserId = null;
        return { status: 'disabled' };
      }

      if (!config.apiKey) return { status: 'missing_key' };
      if (sdkClient) return activateClient(sdkClient);
      if (activationPromise) return activationPromise;

      activationPromise = loadSdk()
        .then((client) => {
          sdkClient = client;
          return statisticsConsent ? activateClient(client) : { status: 'disabled' as const };
        })
        .catch((error: unknown) => {
          onError(error);
          return { status: 'failed' as const };
        })
        .finally(() => {
          activationPromise = null;
        });

      return activationPromise;
    },

    track<Name extends AnalyticsEventName>(
      name: Name,
      properties: AnalyticsEventProperties[Name],
      options?: TrackOptions,
    ): TrackResult {
      return tracker.track(name, properties, options);
    },

    identify(userId: string): void {
      desiredUserId = userId;
      applyIdentity();
    },

    resetIdentity(): void {
      desiredUserId = null;
      appliedUserId = null;

      // 🔴 Só com a captura ativa. Revogado, o optOutPostHog já fez reset(true); repeti-lo aqui
      // mexeria no estado de consentimento que a revogação acabou de gravar.
      if (!capturing() || !sdkClient) return;

      try {
        sdkClient.reset(true);
      } catch (error) {
        onError(error);
      }

      // 🔴 posthog-js (`_reset`) limpa o consentimento junto com a identidade, e com
      // `opt_out_capturing_by_default: true` a captura desliga sem erro. O consentimento de quem
      // usa o dispositivo não mudou com o logout, então é reposto.
      try {
        sdkClient.opt_in_capturing();
      } catch (error) {
        onError(error);
      }
    },

    getClient(): PostHogAnalyticsClient | null {
      return sdkClient;
    },
  };
}

const env = import.meta.env as {
  VITE_POSTHOG_KEY?: string;
  VITE_POSTHOG_HOST?: string;
};

export const analyticsRuntime = createAnalyticsRuntime({
  loadSdk: async () => (await import('posthog-js')).default,
  config: {
    apiKey: env.VITE_POSTHOG_KEY ?? '',
    host: env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
  },
});
